import React from 'react';
import { getOptimizedImageUrl } from '../../utils/imageUtils';
import { getInitials, getColorForName } from '../../utils/avatarUtils';

export type AvatarSize = 'small' | 'medium' | 'large' | 'xlarge';

const SIZE_CLASSES: Record<AvatarSize, string> = {
  small: 'w-10 h-10 text-sm',
  medium: 'w-12 h-12 text-base',
  large: 'w-[100px] h-[100px] text-2xl',
  xlarge: 'w-48 h-48 text-4xl',
};

export interface AvatarProps {
  profilePictureUrl?: string | null;
  firstName?: string | null;
  lastName?: string | null;
  userId?: string | null;
  size?: AvatarSize;
  className?: string;
  alt?: string;
  /** When true and profilePictureUrl is undefined, show loading skeleton */
  loading?: boolean;
  /** Additional classes for the img/initials container (e.g. border, ring) */
  containerClassName?: string;
}

const Avatar: React.FC<AvatarProps> = ({
  profilePictureUrl,
  firstName,
  lastName,
  userId,
  size = 'medium',
  className = '',
  alt,
  loading = false,
  containerClassName = '',
}) => {
  const sizeClass = SIZE_CLASSES[size];
  const baseClasses = 'rounded-full flex items-center justify-center font-bold shrink-0 overflow-hidden';
  const borderRing = size === 'xlarge' || size === 'large'
    ? 'border-4 border-white ring-2 ring-coffee shadow-md'
    : 'border-2 border-coffee';

  // Loading state: show skeleton when profilePictureUrl is undefined and loading
  if (profilePictureUrl === undefined && loading) {
    return (
      <div
        className={`${sizeClass} rounded-full bg-gray-200 animate-pulse ${borderRing} ${containerClassName} ${className}`}
        aria-hidden
      />
    );
  }

  // Has profile picture: render img
  if (profilePictureUrl) {
    const src = getOptimizedImageUrl(profilePictureUrl, size);
    const displayAlt = alt ?? (`${firstName ?? ''} ${lastName ?? ''}`.trim() || 'Profile');
    return (
      <img
        src={src}
        alt={displayAlt}
        className={`${sizeClass} rounded-full object-cover ${borderRing} ${containerClassName} ${className}`}
        loading="lazy"
      />
    );
  }

  // No profile picture: render initials
  const initials = getInitials(firstName, lastName);
  const { bg, text } = getColorForName(firstName, lastName, userId);

  return (
    <div
      className={`${sizeClass} ${baseClasses} ${borderRing} ${containerClassName} ${className}`}
      style={{ backgroundColor: bg, color: text }}
      aria-label={alt ?? (`Avatar for ${firstName ?? ''} ${lastName ?? ''}`.trim() || 'User avatar')}
    >
      {initials}
    </div>
  );
};

export default Avatar;
